import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link2, Copy, Check, Loader2, Trash2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useApi } from '@/hooks/use-api'

/**
 * Customer-facing tracked links (owner request 2026-09-05). Wraps any
 * https:// destination (a booking page, the user's website, a deck) in a
 * short dealcloserautopilot.com/r/<code> link that counts human clicks.
 * Paste the short link into a campaign's "Link the AI sends" or an
 * additional link, and the numbers show up here.
 */

type UserLink = {
  readonly code: string
  readonly label: string
  readonly destination: string
  readonly created_at: string
  readonly share_url: string
  readonly clicks: number
  readonly unique_visitors: number
  readonly clicks_30d: number
  readonly signups: number
}

type LinksResponse = {
  readonly links: readonly UserLink[]
  readonly booked_calls: number
  readonly max_links: number
  readonly share_base: string
}

export const trackedLinksKey = ['settings', 'tracked-links'] as const

type TrackedLinksCardProps = {
  /** Destinations the user already saved elsewhere (offer link, booking link). */
  readonly suggestions?: ReadonlyArray<{ label: string; url: string }>
}

export function TrackedLinksCard({ suggestions = [] }: TrackedLinksCardProps) {
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [destination, setDestination] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: trackedLinksKey,
    queryFn: async (): Promise<LinksResponse> => {
      const res = await apiFetch<LinksResponse>('/api/leads/links')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Could not load tracked links')
      return res.data
    },
    staleTime: 30_000,
  })

  const create = useMutation({
    mutationFn: async (input: { label: string; destination: string }) => {
      const res = await apiFetch<{ link: UserLink }>('/api/leads/links', { method: 'POST', body: input })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Could not create the link')
      return res.data.link
    },
    onSuccess: () => {
      setLabel('')
      setDestination('')
      setFormError(null)
      void queryClient.invalidateQueries({ queryKey: trackedLinksKey })
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Could not create the link'),
  })

  const remove = useMutation({
    mutationFn: async (code: string) => {
      const res = await apiFetch(`/api/leads/links/${code}`, { method: 'DELETE' })
      if (!res.success) throw new Error(res.error ?? 'Could not remove the link')
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: trackedLinksKey }),
  })

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(url)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      /* clipboard blocked — the URL is still visible to select */
    }
  }

  const links = data?.links ?? []
  const atMax = data ? links.length >= data.max_links : false
  const usableSuggestions = suggestions.filter((s) => /^https:\/\//i.test(s.url.trim()))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-cobalt-bright" />
          Tracked links
        </CardTitle>
        <CardDescription>
          Wrap your booking page, website or deck in a short link that counts clicks. Use the short link as the
          &quot;Link the AI sends&quot; in a campaign to see how many leads actually open it.
          {data && data.booked_calls > 0 && ` You have ${data.booked_calls} booked call${data.booked_calls === 1 ? '' : 's'} so far.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : links.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tracked links yet. Create one below.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  <th className="pb-2 pr-3 font-medium">Link</th>
                  <th className="pb-2 pr-3 font-medium text-right">Clicks</th>
                  <th className="pb-2 pr-3 font-medium text-right">Last 30d</th>
                  <th className="pb-2 pr-3 font-medium text-right">People</th>
                  <th className="pb-2 pr-3 font-medium text-right">Signups</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {links.map((l) => (
                  <tr key={l.code} className="border-t">
                    <td className="py-2 pr-3">
                      <p className="font-medium">{l.label}</p>
                      <div className="flex items-center gap-1.5">
                        <code className="text-xs text-muted-foreground">{l.share_url}</code>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => void copy(l.share_url)}
                          aria-label="Copy link"
                        >
                          {copied === l.share_url ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                      <p className="max-w-xs truncate text-[11px] text-muted-foreground" title={l.destination}>→ {l.destination}</p>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.clicks}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.clicks_30d}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{l.unique_visitors}</td>
                    <td className="py-2 pr-3 text-right tabular-nums" title="Only counts when the link points at dealcloserautopilot.com">
                      {l.signups}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-destructive hover:text-destructive"
                        onClick={() => {
                          if (window.confirm(`Remove "${l.label}"? The short link stops working.`)) remove.mutate(l.code)
                        }}
                        disabled={remove.isPending}
                        aria-label={`Remove ${l.label}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form
          className="space-y-3 rounded-md border p-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (!label.trim() || !destination.trim()) {
              setFormError('Give the link a name and an https:// destination.')
              return
            }
            create.mutate({ label: label.trim(), destination: destination.trim() })
          }}
        >
          <p className="text-sm font-medium">New tracked link</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="tl-label">Name</Label>
              <Input id="tl-label" placeholder="Book a call" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} disabled={create.isPending || atMax} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tl-dest">Destination (https://)</Label>
              <Input id="tl-dest" placeholder="https://calendly.com/you/intro" value={destination} onChange={(e) => setDestination(e.target.value)} disabled={create.isPending || atMax} />
            </div>
          </div>
          {usableSuggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">Use:</span>
              {usableSuggestions.map((s) => (
                <button
                  key={s.url}
                  type="button"
                  className="rounded-full border px-2 py-0.5 hover:bg-accent"
                  onClick={() => {
                    setDestination(s.url.trim())
                    if (!label.trim()) setLabel(s.label)
                  }}
                  disabled={create.isPending || atMax}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
          {formError && <p className="text-xs text-destructive">{formError}</p>}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {atMax ? `Limit of ${data?.max_links} links reached.` : 'Clicks by LinkedIn’s preview crawler are not counted.'}
            </p>
            <Button type="submit" size="sm" className="gap-1.5" disabled={create.isPending || atMax}>
              {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Create link
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
